import React, { useState } from 'react';
import { 
  Plus, Trash2, Calendar, CreditCard, Truck, MapPin, 
  FileText, Save, X, ArrowLeft
} from 'lucide-react';
import { 
  suppliers, 
  paymentTerms, 
  shippingMethods 
} from '../../data/purchaseOrderData';
import { products } from '../../data/inventoryData';

const PurchaseOrderCreate = ({ onBack, onSave }) => {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysLater = new Date();
  thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
  const defaultDeliveryDate = thirtyDaysLater.toISOString().split('T')[0];
  
  // Form state
  const [formData, setFormData] = useState({
    supplier_id: '',
    order_date: today,
    expected_delivery_date: defaultDeliveryDate,
    payment_term_id: paymentTerms[0].id,
    shipping_method_id: shippingMethods[0].id,
    shipping_address: '',
    notes: '',
    items: [{
      id: Date.now(),
      product_id: '',
      description: '',
      quantity: 1,
      unit: '個',
      unit_price: 0,
      subtotal: 0
    }]
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;
    
    // Recalculate subtotal if quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = field === 'quantity' ? value : updatedItems[index].quantity;
      const unitPrice = field === 'unit_price' ? value : updatedItems[index].unit_price;
      updatedItems[index].subtotal = quantity * unitPrice;
    }
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Clear error for this item if exists
    if (errors[`items[${index}].${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`items[${index}].${field}`]: undefined
      }));
    }
  };
  
  // Add new item
  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now(),
          product_id: '',
          description: '',
          quantity: 1,
          unit: '個',
          unit_price: 0,
          subtotal: 0
        }
      ]
    }));
  };
  
  // Remove item
  const removeItem = (index) => {
    if (formData.items.length === 1) {
      return; // Keep at least one item
    }
    
    const updatedItems = [...formData.items];
    updatedItems.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };
  
  // Auto-fill product details
  const handleProductSelect = (index, productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      const updatedItems = [...formData.items];
      updatedItems[index].product_id = productId;
      updatedItems[index].description = selectedProduct.description || '';
      updatedItems[index].unit_price = selectedProduct.cost_price || 0;
      updatedItems[index].subtotal = updatedItems[index].quantity * updatedItems[index].unit_price;
      
      setFormData(prev => ({
        ...prev,
        items: updatedItems
      }));
    }
  };
  
  // Auto-fill shipping address from supplier
  const handleSupplierSelect = (supplierId) => {
    const selectedSupplier = suppliers.find(s => s.id === supplierId);
    if (selectedSupplier && selectedSupplier.address) {
      setFormData(prev => ({
        ...prev,
        supplier_id: supplierId,
        shipping_address: selectedSupplier.address
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        supplier_id: supplierId
      }));
    }
  };
  
  // Calculate totals
  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => sum + item.subtotal, 0);
    const tax = subtotal * 0.05; // Assuming 5% tax
    const shippingCost = 0; // Could be dynamic based on shipping method
    const total = subtotal + tax + shippingCost;
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: tax.toFixed(2),
      shipping_cost: shippingCost.toFixed(2),
      total: total.toFixed(2)
    };
  };
  
  const totals = calculateTotals();
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Required fields
    if (!formData.supplier_id) newErrors.supplier_id = '請選擇供應商';
    if (!formData.shipping_address) newErrors.shipping_address = '請輸入運送地址';
    
    // Validate items
    formData.items.forEach((item, index) => {
      if (!item.product_id) newErrors[`items[${index}].product_id`] = '請選擇產品';
      if (item.quantity <= 0) newErrors[`items[${index}].quantity`] = '數量必須大於0';
      if (item.unit_price <= 0) newErrors[`items[${index}].unit_price`] = '單價必須大於0';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Create new PO object with calculated totals
      const newPO = {
        ...formData,
        ...totals,
        status: 'draft',
        created_at: new Date().toISOString(),
        created_by: '系統管理員', // Would come from auth system
        history: [
          {
            id: Date.now(),
            action: 'created',
            action_at: new Date().toISOString(),
            action_by: '系統管理員',
            details: '創建採購訂單'
          }
        ]
      };
      
      // Call the save handler
      if (onSave) onSave(newPO);
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">創建採購訂單</h1>
        </div>
        
        <div className="flex space-x-3">
          <button 
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
          >
            <X className="h-4 w-4" />
            <span>取消</span>
          </button>
          
          <button 
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>儲存訂單</span>
          </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Order Items */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">訂單項目</h2>
            </div>
            
            <div className="p-6 space-y-4">
              {formData.items.map((item, index) => (
                <div key={item.id} className="grid grid-cols-12 gap-4 pb-4 border-b border-gray-100">
                  {/* Product */}
                  <div className="col-span-12 sm:col-span-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">產品</label>
                    <select
                      value={item.product_id}
                      onChange={(e) => handleProductSelect(index, e.target.value)}
                      className={`w-full border ${errors[`items[${index}].product_id`] ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2`}
                    >
                      <option value="">選擇產品</option>
                      {products.map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name} ({product.sku})
                        </option>
                      ))}
                    </select>
                    {errors[`items[${index}].product_id`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`items[${index}].product_id`]}</p>
                    )}
                  </div>
                  
                  {/* Description */}
                  <div className="col-span-12 sm:col-span-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">描述</label>
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-2"
                      placeholder="產品描述"
                    />
                  </div>
                  
                  {/* Quantity */}
                  <div className="col-span-4 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">數量</label>
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                      className={`w-full border ${errors[`items[${index}].quantity`] ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2`}
                      min="1"
                    />
                    {errors[`items[${index}].quantity`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`items[${index}].quantity`]}</p>
                    )}
                  </div>
                  
                  {/* Unit */}
                  <div className="col-span-4 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">單位</label>
                    <input
                      type="text"
                      value={item.unit}
                      onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg p-2"
                    />
                  </div>
                  
                  {/* Unit Price */}
                  <div className="col-span-3 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">單價</label>
                    <input
                      type="number"
                      value={item.unit_price}
                      onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className={`w-full border ${errors[`items[${index}].unit_price`] ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2`}
                      min="0"
                      step="0.01"
                    />
                    {errors[`items[${index}].unit_price`] && (
                      <p className="text-red-500 text-xs mt-1">{errors[`items[${index}].unit_price`]}</p>
                    )}
                  </div>
                  
                  {/* Subtotal */}
                  <div className="col-span-3 sm:col-span-1 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">小計</label>
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        value={item.subtotal.toFixed(2)}
                        className="w-full border border-gray-300 rounded-lg p-2 bg-gray-50"
                        disabled
                      />
                      
                      <button 
                        type="button"
                        onClick={() => removeItem(index)}
                        className="absolute right-0 top-8 p-1 text-red-500 hover:text-red-700"
                        disabled={formData.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Add Item Button */}
              <div className="pt-2">
                <button
                  type="button"
                  onClick={addItem}
                  className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                >
                  <Plus className="h-4 w-4" />
                  <span>新增項目</span>
                </button>
              </div>
            </div>
            
            {/* Order Summary */}
            <div className="p-6 bg-gray-50 border-t border-gray-100">
              <div className="flex flex-col items-end space-y-2">
                <div className="flex justify-between w-full max-w-xs">
                  <span className="text-gray-500">小計:</span>
                  <span className="font-medium">${totals.subtotal}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs">
                  <span className="text-gray-500">稅額 (5%):</span>
                  <span className="font-medium">${totals.tax}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs">
                  <span className="text-gray-500">運費:</span>
                  <span className="font-medium">${totals.shipping_cost}</span>
                </div>
                <div className="flex justify-between w-full max-w-xs pt-2 border-t border-gray-200">
                  <span className="font-medium">總計:</span>
                  <span className="font-bold text-lg">${totals.total}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Notes */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4">備註</h2>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded-lg p-3 h-32"
              placeholder="輸入訂單備註..."
            ></textarea>
          </div>
        </div>
        
        {/* Right Column - Order Info */}
        <div className="space-y-6">
          {/* Supplier Selection */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4">供應商資訊</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">選擇供應商</label>
                <select
                  name="supplier_id"
                  value={formData.supplier_id}
                  onChange={(e) => handleSupplierSelect(e.target.value)}
                  className={`w-full border ${errors.supplier_id ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2`}
                >
                  <option value="">選擇供應商</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                {errors.supplier_id && (
                  <p className="text-red-500 text-xs mt-1">{errors.supplier_id}</p>
                )}
              </div>
            </div>
          </div>
          
          {/* Order Details */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <h2 className="text-lg font-medium mb-4">訂單資訊</h2>
            
            <div className="space-y-4">
              {/* Order Date */}
              <div className="flex items-start space-x-3">
                <div className="p-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-grow">
                  <label className="block text-sm text-gray-500 mb-1">訂單日期</label>
                  <input
                    type="date"
                    name="order_date"
                    value={formData.order_date}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  />
                </div>
              </div>
              
              {/* Expected Delivery Date */}
              <div className="flex items-start space-x-3">
                <div className="p-1">
                  <Calendar className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-grow">
                  <label className="block text-sm text-gray-500 mb-1">預計交期</label>
                  <input
                    type="date"
                    name="expected_delivery_date"
                    value={formData.expected_delivery_date}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  />
                </div>
              </div>
              
              {/* Payment Terms */}
              <div className="flex items-start space-x-3">
                <div className="p-1">
                  <CreditCard className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-grow">
                  <label className="block text-sm text-gray-500 mb-1">付款條件</label>
                  <select
                    name="payment_term_id"
                    value={formData.payment_term_id}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  >
                    {paymentTerms.map(term => (
                      <option key={term.id} value={term.id}>
                        {term.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Shipping Method */}
              <div className="flex items-start space-x-3">
                <div className="p-1">
                  <Truck className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-grow">
                  <label className="block text-sm text-gray-500 mb-1">運送方式</label>
                  <select
                    name="shipping_method_id"
                    value={formData.shipping_method_id}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg p-2"
                  >
                    {shippingMethods.map(method => (
                      <option key={method.id} value={method.id}>
                        {method.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              {/* Shipping Address */}
              <div className="flex items-start space-x-3">
                <div className="p-1">
                  <MapPin className="h-4 w-4 text-gray-400" />
                </div>
                <div className="flex-grow">
                  <label className="block text-sm text-gray-500 mb-1">運送地址</label>
                  <textarea
                    name="shipping_address"
                    value={formData.shipping_address}
                    onChange={handleChange}
                    className={`w-full border ${errors.shipping_address ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2 h-20`}
                    placeholder="輸入運送地址"
                  ></textarea>
                  {errors.shipping_address && (
                    <p className="text-red-500 text-xs mt-1">{errors.shipping_address}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseOrderCreate;
